import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Images, Printer } from 'lucide-react';
import { ApiError, get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { fallbackInterval, useRealtimeInvalidate } from '@/lib/realtime';
import { useUpdateStatusQuery } from '@/lib/updates-api';
import { printDocument } from '@/lib/print';
import { DataTable, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Badge, Button, Input, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { RadiologyAcknowledgeDialog } from './RadiologyAcknowledgeDialog';
import { StudyImagesDialog } from './StudyImagesDialog';
import { hasDetailPath } from './LabBenchErrors';
import { LabBenchReasonDialog } from './LabBenchReasonDialog';
import { SecondOpinionDialog, SecondOpinionsList } from './RadiologySecondOpinion';
import { RadiologySetup } from './RadiologySetup';
import { locationText } from './bench-groups';

interface Study {
  id: string;
  number: string;
  status: string;
  modality: string;
  priority: string;
  orderedAt: string;
  scannedAt: string | null;
  clinicalNotes: string | null;
  referringDoctorName: string | null;
  reportFindings: string | null;
  reportImpression: string | null;
  isAddendum: boolean;
  criticalFinding: boolean;
  criticalAckAt: string | null;
  pregnancyChecked: boolean;
  retakeCount?: number | null;
  retakeReason?: string | null;
  pendingAddenda?: number;
  awaitingPayment?: boolean;
  location?: { wardName: string | null; roomCode: string | null; bedCode: string | null; admissionNo: string } | null;
  studyType: { id: string; code: string; name: string; bodyPart: string | null; requiresContrast: boolean; durationMinutes: number };
  patient: { id: string; mrn: string; fullName: string; sex: string; dob: string | null };
}

interface WorklistPage {
  data: Study[];
  nextCursor: string | null;
}

const MODALITIES = ['xray', 'ultrasound', 'ct', 'mri', 'mammography', 'echo', 'ecg', 'endoscopy'];

const WORKLIST_PAGE_SIZE = 50;

function reportActionLabel(study: Study): string {
  if (['verified', 'released'].includes(study.status) || study.isAddendum) return 'Addendum';
  return study.reportFindings ? 'Edit report' : 'Report';
}

/**
 * Radiology worklist and reporting (SRS 8.2).
 *
 * The technician's tabs and the radiologist's tabs are the same screen because
 * the study is the same object; what each sees is decided by permission, not by
 * a separate application.
 */
export function RadiologyPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('worklist');
  const [modality, setModality] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [referrerDraft, setReferrerDraft] = useState('');
  const [referrerTerm, setReferrerTerm] = useState('');
  const [acknowledging, setAcknowledging] = useState<Study | null>(null);
  const [imagesFor, setImagesFor] = useState<Study | null>(null);
  const [retaking, setRetaking] = useState<Study | null>(null);
  const [asking, setAsking] = useState<Study | null>(null);
  const commitReferrer = () => setReferrerTerm(referrerDraft.trim());
  const resetFilters = () => {
    setModality('');
    setSearchTerm('');
    setReferrerDraft('');
    setReferrerTerm('');
  };
  const canWriteReports = can('radiology.report.write');
  const canAcknowledge = can('radiology.critical.acknowledge', 'radiology.report.verify');

  const statusForTab: Record<string, string | undefined> = {
    worklist: undefined,
    toReport: 'scanned',
    toVerify: 'reported',
    released: 'released',
  };

  const live = useRealtimeInvalidate(['radiology-worklist'], null, [['radiology-worklist'], ['radiology-counts'], ['radiology-second-opinions']]);
  const counts = useQuery({
    queryKey: ['radiology-counts'],
    queryFn: () => get<Record<string, number>>('/radiology/worklist/counts'),
    refetchInterval: fallbackInterval(live, 120_000),
    staleTime: 30_000,
  });
  const retake = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => post(`/radiology/studies/${id}/retake`, { reason }),
    onSuccess: () => {
      toast.success(tr('radiology', 'sent_back_to_be_imaged_again'));
      setRetaking(null);
      void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
      void queryClient.invalidateQueries({ queryKey: ['radiology-counts'] });
    },
    onError: (error) => toast.error(tr('radiology', 'could_not_ask_for_a_retake'), describeError(error)),
  });
  const verifyAddendum = useMutation({
    mutationFn: (id: string) => post(`/radiology/studies/${id}/verify`, { release: true }),
    onSuccess: () => {
      toast.success(tr('radiology', 'addendum_verified'));
      void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
    },
    onError: (error) => toast.error(tr('radiology', 'could_not_verify_the_addendum'), describeError(error)),
  });
  const install = useUpdateStatusQuery({ refetchInterval: false });
  const onLocalServer = install.data?.deploymentMode === 'LOCAL';
  const sendNow = useMutation({
    mutationFn: () => post('/updates/sync/push'),
    onSuccess: () => toast.success(tr('radiology', 'sending_waiting_images_to_the_online_copy_now')),
    onError: (error) => toast.error(tr('radiology', 'could_not_send_now'), describeError(error)),
  });
  const studies = useInfiniteQuery({
    queryKey: ['radiology-worklist', tab, modality, searchTerm, referrerTerm],
    initialPageParam: '',
    enabled: tab !== 'secondOpinions' && tab !== 'setup',
    queryFn: ({ pageParam }) => {
      const params = qs({
        status: statusForTab[tab],
        modality: modality || undefined,
        q: searchTerm || undefined,
        referrer: referrerTerm || undefined,
        limit: WORKLIST_PAGE_SIZE,
        cursor: pageParam,
      });
      return get<WorklistPage>(`/radiology/worklist${params}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (query) => {
      if ((query.state.data?.pages.length ?? 0) > 1) return false;
      const poll = fallbackInterval(live, 60_000);
      return poll ? poll() : false;
    },
  });
  const rows = studies.data?.pages.flatMap((page) => page.data);

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      put(`/radiology/studies/${id}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
    },
    onError: (error) => toast.error(tr('radiology', 'could_not_update_the_study'), describeError(error)),
  });

  const [overriding, setOverriding] = useState<{ id: string; message: string } | null>(null);
  const verify = useMutation({
    mutationFn: ({ id, overrideReason }: { id: string; overrideReason?: string }) =>
      post(`/radiology/studies/${id}/verify`, { release: true, ...(overrideReason ? { overrideReason } : {}) }),
    onSuccess: () => {
      setOverriding(null);
      toast.success(tr('radiology', 'report_released'), tr('radiology', 'the_patient_has_been_notified'));
      void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
    },
    onError: (error, variables) => {
      if (error instanceof ApiError && hasDetailPath(error, 'overrideReason')) {
        setOverriding({ id: variables.id, message: describeError(error) });
        return;
      }
      if (error instanceof ApiError && error.status === 409 && /different radiologist/i.test(error.message)) {
        toast.error(
          tr('radiology', 'a_different_radiologist_must_verify_this_report'),
          tr('radiology', 'your_hospital_does_not_let_the_person_who_wrote'),
        );
        return;
      }
      toast.error(tr('radiology', 'could_not_release_the_report'), describeError(error));
    },
  });

  const printStudy = async (id: string) => {
    try {
      const doc = await get<{ html: string }>(`/radiology/studies/${id}/print`);
      printDocument(doc.html);
    } catch (error) {
      toast.error(tr('radiology', 'could_not_print_the_report'), describeError(error));
    }
  };

  return (
    <>
      <PageHeader
        title={tr('radiology', 'radiology')}
        description={tr('radiology', 'modality_worklist_reporting_and_release')}
        actions={
          <div className="flex gap-2">
            {onLocalServer && can('radiology.worklist') && (
              <Button variant="outline" onClick={() => sendNow.mutate()} loading={sendNow.isPending}>
                {tr('radiology', 'send_images_now')}
              </Button>
            )}
            {can('order.write') && <Button onClick={() => navigate('/radiology/orders/new')}>{tr('radiology', 'order_study')}</Button>}
          </div>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'worklist', label: tr('radiology', 'all_studies') },
          { id: 'toReport', label: tr('radiology', 'to_report'), badge: counts.data?.scanned || undefined },
          { id: 'toVerify', label: tr('radiology', 'to_verify'), badge: counts.data?.reported || undefined },
          { id: 'released', label: tr('radiology', 'released') },
          ...(canWriteReports ? [{ id: 'secondOpinions', label: tr('radiology', 'second_opinions') }] : []),
          ...(can('radiology.import.manage', 'radiology.master.manage') ? [{ id: 'setup', label: tr('radiology', 'setup') }] : []),
        ]}
      />

      {tab === 'setup' ? (
        <RadiologySetup />
      ) : tab === 'secondOpinions' ? (
        <SecondOpinionsList onOpenStudy={(studyId) => navigate(`/radiology/studies/${studyId}/report`)} />
      ) : (
        <>

      <SearchFilterBar
        search={{ value: searchTerm, onChange: setSearchTerm, placeholder: tr('radiology', 'name_mrn_study_no_phone_cnic'), label: tr('radiology', 'search_studies') }}
        filters={
          <>
            <FilterField label={tr('radiology', 'modality')}>
              <Select className="w-44" value={modality} onChange={(e) => setModality(e.target.value)}>
                <option value="">{tr('radiology', 'all_modalities')}</option>
                {MODALITIES.map((m) => (
                  <option key={m} value={m}>
                    {humanize(m, 'radiology_modality')}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label={tr('radiology', 'referring_doctor')}>
              <Input
                className="w-56"
                aria-label={tr('radiology', 'referring_doctor')}
                placeholder={tr('radiology', 'referring_doctor')}
                value={referrerDraft}
                onChange={(e) => setReferrerDraft(e.target.value)}
                onBlur={commitReferrer}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitReferrer();
                  }
                }}
              />
            </FilterField>
          </>
        }
        onReset={modality || searchTerm || referrerTerm ? resetFilters : undefined}
      />

      <DataTable
        rows={rows}
        loading={studies.isLoading} refreshing={studies.isFetching}
        partial={Boolean(studies.hasNextPage)}
        error={rows ? undefined : studies.error}
        rowKey={(row) => row.id}
        empty={tr('radiology', 'nothing_in_this_stage')}
        footer={
          rows && (studies.hasNextPage || studies.isFetchNextPageError) ? (
            <div className="flex w-full flex-col items-center gap-2">
              {studies.hasNextPage && (
                <Button variant="outline" size="sm" onClick={() => void studies.fetchNextPage()} loading={studies.isFetchingNextPage}>
                  {tr('radiology', 'load_more')}
                </Button>
              )}
              {studies.isFetchNextPageError && <p className="text-center text-sm text-destructive">{describeError(studies.error)}</p>}
            </div>
          ) : undefined
        }
        columns={[
          {
            header: tr('radiology', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient.mrn}
                  {row.location ? ` · ${locationText({ ...row.location, wardId: null })}` : ''}
                </p>
              </div>
            ),
          },
          {
            header: tr('radiology', 'study'),
            sort: (row) => row.studyType.name,
            cell: (row) => (
              <div>
                <p>{row.studyType.name}</p>
                <p className="text-xs text-muted-foreground">
                  {humanize(row.modality, 'radiology_modality')}
                  {row.studyType.bodyPart ? ` · ${row.studyType.bodyPart}` : ''}
                  {row.studyType.requiresContrast ? ' · contrast' : ''}
                </p>
                {row.referringDoctorName && (
                  <p className="text-xs text-muted-foreground">{tr('radiology', 'referred_by')} {row.referringDoctorName}</p>
                )}
              </div>
            ),
          },
          { header: tr('radiology', 'accession'), sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
          { header: tr('radiology', 'ordered'), sort: (row) => row.orderedAt, cell: (row) => dateTime(row.orderedAt) },
          {
            header: tr('radiology', 'status'),
            sort: (row) => row.status,
            cell: (row) => (
              <div className="flex gap-1">
                <StatusBadge status={row.status} />
                {row.priority !== 'routine' && <Badge variant="warning">{humanize(row.priority)}</Badge>}
                {row.criticalFinding && !row.criticalAckAt && <Badge variant="destructive">{tr('radiology', 'critical')}</Badge>}
                {(row.retakeCount ?? 0) > 0 && <Badge variant="secondary" title={row.retakeReason ?? undefined}>{tr('radiology', 'retake')}</Badge>}
                {(row.pendingAddenda ?? 0) > 0 && <Badge variant="warning">{tr('radiology', 'addendum_to_verify')}</Badge>}
                {row.awaitingPayment && <Badge variant="warning">{tr('radiology', 'awaiting_payment')}</Badge>}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {row.status === 'ordered' && !row.awaitingPayment && can('radiology.worklist') && (
                  <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: row.id, status: 'arrived' })}>
                    {tr('radiology', 'arrived')}
                  </Button>
                )}
                {row.status === 'arrived' && !row.awaitingPayment && can('radiology.worklist') && (
                  <Button size="sm" onClick={() => setStatus.mutate({ id: row.id, status: 'scanned' })}>
                    {tr('radiology', 'scan_done')}
                  </Button>
                )}
                {row.criticalFinding && !row.criticalAckAt && canAcknowledge && (
                  <Button size="sm" variant="destructive" onClick={() => setAcknowledging(row)}>
                    {tr('radiology', 'acknowledge')}
                  </Button>
                )}
                {['scanned', 'reported', 'verified', 'released'].includes(row.status) && canWriteReports && (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/radiology/studies/${row.id}/report`)}>
                    {reportActionLabel(row)}
                  </Button>
                )}
                {!canWriteReports && row.reportFindings && (
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/radiology/studies/${row.id}/report`)}>
                    {tr('radiology', 'view_report')}
                  </Button>
                )}
                {row.status === 'reported' && Boolean(row.reportFindings) && can('radiology.report.verify') && (
                  <Button
                    size="sm"
                    onClick={() => verify.mutate({ id: row.id })}
                    loading={verify.isPending && verify.variables?.id === row.id}
                    disabled={verify.isPending}
                  >
                    {tr('radiology', 'verify_and_release')}
                  </Button>
                )}
                {row.status === 'released' && (row.pendingAddenda ?? 0) > 0 && can('radiology.report.verify') && (
                  <Button size="sm" onClick={() => verifyAddendum.mutate(row.id)} loading={verifyAddendum.isPending && verifyAddendum.variables === row.id}>
                    {tr('radiology', 'verify_addendum')}
                  </Button>
                )}
                {['scanned', 'reported'].includes(row.status) && canWriteReports && (
                  <Button size="sm" variant="ghost" onClick={() => setRetaking(row)}>
                    {tr('radiology', 'retake')}
                  </Button>
                )}
                {['scanned', 'reported', 'verified', 'released'].includes(row.status) && canWriteReports && (
                  <Button size="sm" variant="ghost" onClick={() => setAsking(row)}>
                    {tr('radiology', 'second_opinion')}
                  </Button>
                )}
                {can('radiology.worklist', 'radiology.report.write', 'radiology.report.verify') && (
                  <Button size="sm" variant="ghost" aria-label={tr('radiology', 'images')} title={tr('radiology', 'images')} onClick={() => setImagesFor(row)}>
                    <Images className="h-4 w-4" /> {tr('radiology', 'images')}
                  </Button>
                )}
                {['verified', 'released'].includes(row.status) && (
                  <Button size="sm" variant="ghost" aria-label={tr('radiology', 'print_report')} title={tr('radiology', 'print_report')} onClick={() => void printStudy(row.id)}>
                    <Printer className="h-4 w-4" /> {tr('radiology', 'print')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
        </>
      )}

      {retaking && (
        <LabBenchReasonDialog
          title={tr('radiology', 'retake_x', { name: retaking.studyType.name })}
          description={tr('radiology', 'x_x_the_study_goes_back_to_the_technician_the', { fullName: retaking.patient.fullName, number: retaking.number })}
          label={tr('radiology', 'why_is_it_being_imaged_again')}
          confirmLabel={tr('radiology', 'ask_for_a_retake')}
          minLength={3}
          pending={retake.isPending}
          onSubmit={(reason) => retake.mutate({ id: retaking.id, reason })}
          onClose={() => setRetaking(null)}
        />
      )}
      {asking && (
        <SecondOpinionDialog
          studyId={asking.id}
          studyLabel={`${asking.patient.fullName} · ${asking.studyType.name} · ${asking.number}`}
          onClose={() => setAsking(null)}
        />
      )}

      {imagesFor && (
        <StudyImagesDialog study={imagesFor} canEdit={can('radiology.worklist', 'radiology.report.write')} onClose={() => setImagesFor(null)} />
      )}

      {acknowledging !== null && (
        <RadiologyAcknowledgeDialog study={acknowledging} onClose={() => setAcknowledging(null)} />
      )}
      {overriding && (
        <LabBenchReasonDialog
          title={tr('radiology', 'release_a_report_that_is_not_settled')}
          description={overriding.message}
          label={tr('radiology', 'reason_for_releasing_anyway')}
          confirmLabel={tr('radiology', 'release_with_reason')}
          minLength={5}
          pending={verify.isPending}
          onSubmit={(reason) => verify.mutate({ id: overriding.id, overrideReason: reason })}
          onClose={() => setOverriding(null)}
        />
      )}
    </>
  );
}
